// faro.js — Grafana Faro RUM bootstrap (Real User Monitoring).
//
// Captures web-vitals (LCP/CLS/INP/FCP/TTFB), uncaught errors, unhandled
// rejections, session tracking, page loads, and (via web tracing) W3C trace
// context propagated to our backends so a browser span links to the server
// trace in Tempo. Shared/reusable — identical to the inventory app's module.
import {
  initializeFaro,
  getWebInstrumentations,
  ReactIntegration,
} from "@grafana/faro-react";
import { TracingInstrumentation } from "@grafana/faro-web-tracing";

let faro = null;

function escapeRegExp(s) {
  return String(s).replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

// Best-effort PII reduction: strip query strings. Wrapped so a shape change in
// Faro never breaks the app — on any error the item is sent unchanged.
function redactQueryStrings(item) {
  try {
    const strip = (u) => (typeof u === "string" ? u.split("?")[0] : u);
    if (item?.meta?.page?.url) item.meta.page.url = strip(item.meta.page.url);
    if (item?.payload?.page?.url) item.payload.page.url = strip(item.payload.page.url);
    if (item?.payload?.url) item.payload.url = strip(item.payload.url);
  } catch (_) {
    /* never block telemetry on sanitization */
  }
  return item;
}

/**
 * Initialize RUM. Idempotent. Returns the Faro instance (or null if no
 * collectorUrl is configured, e.g. local dev — RUM is then silently disabled).
 */
export function initRUM({
  appName,
  environment,
  collectorUrl,
  apiAllowedUrls = [],
  version = "0.0.0",
  sessionSampleRate = 1.0,
  appKey,
} = {}) {
  if (faro) return faro;
  if (!collectorUrl) return null; // not configured (local) → no-op

  try {
    faro = initializeFaro({
      url: appKey ? `${collectorUrl}?apiKey=${encodeURIComponent(appKey)}` : collectorUrl,
      app: { name: appName, version, environment },
      sessionTracking: { enabled: true, samplingRate: sessionSampleRate },
      instrumentations: [
        ...getWebInstrumentations({ captureConsole: false }),
        new TracingInstrumentation({
          instrumentationOptions: {
            propagateTraceHeaderCorsUrls: apiAllowedUrls
              .filter(Boolean)
              .map((u) => new RegExp(escapeRegExp(u))),
          },
        }),
        new ReactIntegration(),
      ],
      beforeSend: redactQueryStrings,
    });
  } catch (e) {
    // RUM must never break the app.
    // eslint-disable-next-line no-console
    console.error("[rum] Faro init failed:", e && e.message);
    faro = null;
  }
  return faro;
}

export function getFaro() {
  return faro;
}

export function reportError(error, context) {
  try {
    faro?.api?.pushError(error, context ? { context } : undefined);
  } catch (_) {}
}

export function trackRoute(route) {
  try {
    faro?.api?.pushEvent("route_change", { route });
  } catch (_) {}
}
